// MACHINERY V2 (Stroje) — the spec sheet's TOOLBAR ROW: metric/imperial segment
// + collapse-all, in board 23's order or artboard 1006's (M2.16d fix pass).
//
// WHY IT IS ITS OWN FILE. It came out of SpecSheetView.tsx for the same reason
// `PartsToolbar` lives in partsTabChrome.tsx and `SrToolbar` in srTabChrome.tsx
// — a toolbar is chrome, and its host was near the 300-line cap. It is NOT
// folded into specChrome.tsx: that file is inert decoration (glyphs, dividers,
// a count pill) and this one is state-driven controls, and the merge put
// specChrome 52 lines over the cap.
//
// ONE COLLAPSE-ALL NODE, TWO ORDERS (review item 8). The button used to be
// written out twice, once inside each order's guard, differing only in
// position. Two copies of one control is two places to change its label, its
// gate or its size — and one of the copies had already drifted (the phone one
// had lost `height: 26`). It is defined once below and placed in two rows.
//
// ONE PILL, TWO HIT TARGETS (review F6). See `Segment`.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";

/** The metric/imperial pill as the artboard paints it — 10.5px mono, 3x10
 *  padding, 5px radius. Extracted so the plain and the touch-wrapped forms
 *  cannot drift into two different pills. */
const segmentPill = (on: boolean): React.CSSProperties => ({
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  padding: "3px 10px",
  borderRadius: 5,
  background: on ? "var(--card)" : "transparent",
  color: on ? "var(--mod-accent)" : "var(--faint)",
  fontWeight: on ? 600 : 400,
  border: "1px solid " + (on ? "var(--border)" : "transparent"),
  whiteSpace: "nowrap",
});

/**
 * One arm of the metric/imperial segment.
 *
 * >>> BELOW 1280 THE PAINT DOES NOT GROW; A TRANSPARENT WRAPPER DOES (M2.16d
 * review, F6). <<< The floor used to be delivered by a CSS rule matching
 * `button[aria-pressed]` inside the profile, which turned this two-arm segment
 * into an 88x44 block across the whole tablet tier. The module's own idiom for a
 * control that cannot meet 44px by growing is `mobile/TouchChip.tsx` /
 * `partsAtoms.tsx`'s checkbox: transparent 44px button, painted span inside.
 *
 * `touch` OMITTED IS TODAY'S ELEMENT, BYTE FOR BYTE — one button carrying the
 * pill style, no wrapper. That is what every host renders at >= 1280.
 *
 * >>> IT IS NO LONGER "WHAT THE TWO KATALÓG HOSTS KEEP AT EVERY WIDTH"
 * (corrected M2.16e). <<< `touch` was reachable only from the profile until
 * this pass, so the katalóg's mobile detail painted a ~28px segment beside a
 * collapse-all the module-wide `.btn` floor had already raised to 44, in the
 * same row. `touch` now means the VIEWPORT and every below-1280 host passes
 * it; what stays profile-only is the toolbar REORDER below, on `phoneOrder`.
 */
const Segment: React.FC<{
  label: string;
  on: boolean;
  onClick: () => void;
  touch?: boolean;
}> = ({ label, on, onClick, touch }) => {
  if (!touch) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={on}
        style={{ ...segmentPill(on), cursor: "pointer" }}
      >
        {label}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      style={{
        // 44px minimum on both axes, transparent. No `minWidth: 0` follows this
        // pair; one would silently void it.
        minHeight: 44,
        minWidth: 44,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: 0,
        background: "transparent",
        padding: 0,
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      <span style={segmentPill(on)}>{label}</span>
    </button>
  );
};

export interface SpecSheetToolbarProps {
  /** Mono eyebrow on the left. Dropped entirely in the phone order. */
  label: string;
  metric: boolean;
  onMetric: (metric: boolean) => void;
  allExpanded: boolean;
  onToggleAll: () => void;
  /** There is something collapsible to act on — the button is not rendered
   *  otherwise, in EITHER order. */
  canCollapseAll: boolean;
  /** Board 23's phone order — the PROFILE's, gated on `profileHost && phone`
   *  one level up. See SpecSheetView for why this is not `phone`. */
  phoneOrder?: boolean;
  /** Below 1280, on ANY host. The viewport's 44px law, not a page scope. */
  touch?: boolean;
}

/**
 * The sheet's toolbar row.
 *
 * TWO ORDERS, ONE SET OF CONTROLS (M2.16d review, item 8). Board 23 puts the
 * metric/imperial segment on the LEFT and "Zbaliť všetko" on the right, and
 * drops the mono intro line — at 390px the label plus a spacer plus two controls
 * wrapped onto three lines above the first section. Artboard 1006's order is
 * label · spacer · collapse-all · segment.
 *
 * The collapse-all button used to be WRITTEN TWICE, once inside each order's
 * guard; it is now one node placed in two rows, which is exactly what
 * `PartsToolbar` and `SrToolbar` do with their search field and chips. The
 * single node carries artboard 1006's `height: 26`; below 1280 the CSS `.btn`
 * floor raises the USED height to 44 without touching this declaration,
 * because `min-height` outranks `height` regardless of origin.
 *
 * THAT FLOOR IS MODULE-WIDE SINCE M2.16e, AND THE DIFFERENCE MATTERS HERE.
 * When this comment was written the floor was scoped to `.mv2-profile-touch`,
 * so it covered the Specifikácie tab and NOT the other surface this component
 * renders on: CatalogDetailMobile puts SpecSheetView in the katalóg detail
 * below 1280, outside that scope, where this button stayed 26px. It is 44 on
 * both surfaces now.
 */
export const SpecSheetToolbar: React.FC<SpecSheetToolbarProps> = ({
  label,
  metric,
  onMetric,
  allExpanded,
  onToggleAll,
  canCollapseAll,
  phoneOrder,
  touch,
}) => {
  const { translate } = useTranslation("myMachinery");

  const spacer = <span style={{ flex: 1 }} />;

  const collapseAll = canCollapseAll ? (
    <button
      type="button"
      className="btn btn-ghost"
      onClick={onToggleAll}
      style={{ height: 26, fontSize: 11.5, whiteSpace: "nowrap" }}
    >
      {translate(allExpanded ? "myMachinery:collapseAll" : "myMachinery:expandAll")}
    </button>
  ) : null;

  const segments = (
    <div
      style={{
        display: "inline-flex",
        gap: 2,
        padding: 2,
        background: "var(--bg-soft)",
        border: "1px solid var(--border)",
        borderRadius: 7,
      }}
    >
      <Segment
        label={translate("myMachinery:metric")}
        on={metric}
        onClick={() => onMetric(true)}
        touch={touch}
      />
      <Segment
        label={translate("myMachinery:imperial")}
        on={!metric}
        onClick={() => onMetric(false)}
        touch={touch}
      />
    </div>
  );

  const row: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    minWidth: 0,
  };

  if (phoneOrder) {
    return (
      <div style={row}>
        {segments}
        {spacer}
        {collapseAll}
      </div>
    );
  }

  return (
    <div style={row}>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--faint)",
          letterSpacing: "0.04em",
          minWidth: 0,
        }}
      >
        {label}
      </span>
      {spacer}
      {collapseAll}
      {segments}
    </div>
  );
};
